import { APIClient } from "./api_helper";
import { CREATE_MISSING_REFERENCES } from "./importContract";
import * as url from "./url_helper";
import { projectToDto, shouldProjectPayload } from "./dtoWhitelist";

const api = new APIClient();

const outboundFor = (dtoName) => (data) =>
  shouldProjectPayload() ? projectToDto(dtoName, data) : data;

/**
 * 為單一 DC Keeper 資源產生一組 CRUD 呼叫函式，對應 mock/真後端的 REST 端點。
 *
 * 用法：
 *   const usersApi = createResourceApi(url.DCK.USERS);
 *   usersApi.list({ page: 1, keyword: "王" });  // → { data, total, page, pageSize }
 *   usersApi.get(3);
 *   usersApi.create({ name: "新用戶" });
 *   usersApi.update(3, { status: "停用" });
 *   usersApi.remove(3);
 *
 * saga 或元件皆可直接用；切換真後端時介面不變（只需 REACT_APP_USE_MOCK=false）。
 */
export function createResourceApi(base) {
  return {
    list: (params = {}) => api.get(base, { params }),
    get: (id) => api.get(`${base}/${id}`),
    create: (data) => api.create(base, data),
    update: (id, data) => api.update(`${base}/${id}`, data),
    remove: (id) => api.delete(`${base}/${id}`),
    // 批次匯入（整批一單）：rows 為解析後的資料陣列
    importRows: (rows) => api.create(`${base}/import`, { rows }),
  };
}

// 三關審核 API（共用引擎）
export const approvalsApi = {
  submit: (payload) => api.create(url.DCK.APPROVALS, payload),
  get: (id) => api.get(`${url.DCK.APPROVALS}/${id}`),
  list: (params = {}) => api.get(url.DCK.APPROVALS, { params }),
  approve: (id, payload) => api.create(`${url.DCK.APPROVALS}/${id}/approve`, payload),
  reject: (id, payload) => api.create(`${url.DCK.APPROVALS}/${id}/reject`, payload),
  // 撤回(ticket 06):只對審核中的單,直接作廢不產生新單
  withdraw: (id, payload) => api.create(`${url.DCK.APPROVALS}/${id}/withdraw`, payload),
};

/**
 * 密碼重設 API（ticket 12）。
 * requestReset 兩種入口共用:管理者代為觸發帶 userId,使用者自己來帶 email。
 * 回應一律是「已寄出」,不會透露該 Email 是否存在。
 */
export const authApi = {
  login: (payload) => api.create(`${url.DCK.AUTH}/login`, payload),
};

export const auditArchiveApi = {
  list: (params = {}) => api.get(url.DCK.AUDIT_ARCHIVE, { params }),
};

// ⚠️ 逐步汰換中(ticket 09):忘記密碼/改密碼已切到下方 passwordApi(真後端契約);
// 這裡剩 mock-only 的舊流程(requestReset/checkToken/resetPassword),隨頁面遷移移除。
// 管理者代設密碼(adminSetPassword)已於 8/12 確認非需求,連同 UI 一併移除
export const passwordResetApi = {
  requestReset: (payload) => api.create(`${url.DCK.AUTH}/reset-request`, payload),
  checkToken: (token) => api.get(`${url.DCK.AUTH}/reset-token/${encodeURIComponent(token)}`),
  resetPassword: (payload) => api.create(`${url.DCK.AUTH}/reset-password`, payload),
  // 登入後自己改密碼(要驗舊密碼),與忘記密碼的 token 流程分開
  changePassword: (payload) => api.create(`${url.DCK.AUTH}/change-password`, payload),
};

// 各模組現成的 API（隨 Sprint 逐步擴充）
export const rolesApi = createResourceApi(url.DCK.ROLES);
export const usersApi = createResourceApi(url.DCK.USERS);
export const workItemsApi = createResourceApi(url.DCK.WORK_ITEMS);
export const roomsApi = createResourceApi(url.DCK.ROOMS);
export const cabinetsApi = createResourceApi(url.DCK.CABINETS);
export const hardwareApi = createResourceApi(url.DCK.HARDWARE);
export const notificationsApi = createResourceApi(url.DCK.NOTIFICATIONS);
export const notifyRulesApi = createResourceApi(url.DCK.NOTIFY_RULES);
export const notifyEventSettingsApi = createResourceApi(url.DCK.NOTIFY_EVENT_SETTINGS);
// ipsApi(/api/ips)已於票 9月交付/16 汰換 —— IP 資訊改走真後端 DcIpAddress(dcIpAddressesApi)。
// mock 層的 "ip" store 仍由 registerResource 註冊(審核引擎回寫、批次匯入測試用),
// 但頁面不再打 /api/ips;不留 mock 版本以免被「順手」接回去(ADR-0001 逐資源汰換)。
export const projectsApi = createResourceApi(url.DCK.PROJECTS);
export const contractsApi = createResourceApi(url.DCK.CONTRACTS);
export const vendorsApi = createResourceApi(url.DCK.VENDORS);
export const softwareApi = createResourceApi(url.DCK.SOFTWARE);
// vulnsApi(/api/vulns)已於票 29 汰換 —— CVE/KEV 改走真後端
// AssetVersion/GetVulnerabilities(見下方 assetVersionApi)。這裡不留 mock 版本,
// 免得有人「順手」接回去,兩個資料源同時活著會很難查(ADR-0001 的逐資源汰換)。
export const monitorDevicesApi = createResourceApi(url.DCK.MONITOR_DEVICES);

// ═══════════ 真後端(Web API Backend v1.00)串接層 — ADR-0001~0003 ═══════════
// mock REST(上方)與真後端契約刻意分成兩套;逐資源切換,切換完成的資源汰換上方對應項。

// 登入認證(Backend v1.00 Authenticate)
// POST /api-backend/Authenticate/GetToken
//   body: AuthenticateAdminRequest { username, password }
//   200 : AuthenticateResponse { username, customer_id, customer_guid, token }
//   400 / 401: string(帳密錯誤等;由 api_helper 攔截器轉譯為安全文案)
// 註:名為 backendAuthApi 以與上方 mock 時代的 authApi 區隔;真後端全面接通後合併。
export const backendAuthApi = {
  getToken: ({ username, password }) =>
    api.create(url.DCK_BACKEND.AUTH_GET_TOKEN, { username, password }),
};

/**
 * 真後端資源 API 工廠(路徑慣例 /api-backend/{Controller}/{Action})。
 * - 分頁參數 pageIndex(0 起算)/ pageSize
 * - 回傳分頁信封 { total_count, total_pages, has_previous_page, has_next_page,
 *   items[], page_index, page_size },JSON 屬性 snake_case
 * - Update 為 PUT 到 /Update,body 含 id(非 /:id 路徑參數)
 * 所有端點需 JWT(登入後由 setAuthorization 掛 Bearer header),未帶 token 回 401。
 */
/**
 * 錯誤 body 是純字串原因(「資產編號 X 已存在」)時,畫面直接顯示 —— 不分資源,
 * 過濾在 errorMessages.translateApiError(2026-10-21 起不再需要逐資源標 trustStringError)。
 */
export function createBackendResourceApi(base, { dto } = {}) {
  // 寫入邊界白名單(helpers/dtoWhitelist):給了 dto 名稱、而且請求會打到真後端時,
  // 只送 swagger 有的鍵(三個資產 DTO 都是 additionalProperties:false)。全 mock 時原樣送,
  // mock 才收得到 WF 需要的契約外過渡欄位。
  const outbound = (data) => (dto && shouldProjectPayload() ? projectToDto(dto, data) : data);
  return {
    getAll: (params = {}) => api.get(`${base}/GetAll`, { params }),
    getById: (id) => api.get(`${base}/GetById/${id}`),
    create: (data) => api.create(`${base}/Create`, outbound(data)),
    update: (data) => api.update(`${base}/Update`, outbound(data)),
    remove: (id) => api.delete(`${base}/Delete/${id}`),
  };
}

// 權限設定(DCK-01-01) — DcCustomerRole
// GetAll query: keywords, showHidden, pageIndex, pageSize → 分頁 CustomerRoleDto
export const customerRolesApi = {
  ...createBackendResourceApi(url.DCK_BACKEND.CUSTOMER_ROLE, { dto: "CustomerRoleDto" }),
  // 角色編輯畫面渲染用:模組列/欄位級/範本
  getPermissionSchema: () => api.get(`${url.DCK_BACKEND.CUSTOMER_ROLE}/GetPermissionSchema`),
};

// 使用者資訊(DCK-01-02) — DcCustomer
// GetAll query: keyword(員編/姓名/信箱模糊), unitName, jobTitle,
//   customerRoleId(0=不限), isActive, pageIndex, pageSize → 分頁 DcCustomerListItemDto
// Create/Update body: DcCustomerDto（customerToDto；additionalProperties:false）
// 密碼編輯留空=不變更;400 驗證錯誤回 string[]
export const dcCustomersApi = {
  ...createBackendResourceApi(url.DCK_BACKEND.CUSTOMER, { dto: "DcCustomerDto" }),
  // ⚠️ 批次匯入:Backend v1.00 未提供 DcCustomer 匯入端點,此路徑為前端預留、
  // 目前僅 mock 支援;真後端契約確定後對齊。
  importRows: (rows) => api.create(`${url.DCK_BACKEND.CUSTOMER}/Import`, { rows }),
};

// 密碼流程(ticket 09;文件 8/11)— 全部掛在 DcCustomer controller 下。
// ChangePassword 需 JWT(當前使用者);Recovery 系列是登入前流程(信箱連結帶
// token+customerGuid)。400 驗證錯誤為 string[](逐欄),轉譯層以換行合併。
export const passwordApi = {
  // GET 模型:password_must_be_changed/password_expired = 首登改密/密碼逾期的資料源
  getChangePasswordModel: () => api.get(`${url.DCK_BACKEND.CUSTOMER}/ChangePassword`),
  // 契約 model 的其餘欄位在這層補滿:呼叫端只給真正的輸入欄位,
  // 填充細節(custom_properties 等)不外漏到頁面
  changePassword: (model) =>
    api.create(`${url.DCK_BACKEND.CUSTOMER}/ChangePassword`, {
      password_must_be_changed: false, password_expired: false, custom_properties: {}, ...model,
    }),
  getRecoveryModel: () => api.get(`${url.DCK_BACKEND.CUSTOMER}/PasswordRecovery`),
  recoverySend: (model) =>
    api.create(`${url.DCK_BACKEND.CUSTOMER}/PasswordRecoverySend`, {
      email: "", result: "", display_captcha: false, custom_properties: {}, ...model,
    }),
  recoveryConfirm: ({ token, customerGuid }) =>
    api.create(
      `${url.DCK_BACKEND.CUSTOMER}/PasswordRecoveryConfirm?token=${encodeURIComponent(token)}&customerGuid=${encodeURIComponent(customerGuid)}`
    ),
  recoveryConfirmSubmit: ({ token, customerGuid }, model) =>
    api.create(
      `${url.DCK_BACKEND.CUSTOMER}/PasswordRecoveryConfirmPOST?token=${encodeURIComponent(token)}&customerGuid=${encodeURIComponent(customerGuid)}`,
      {
        new_password: "", confirm_new_password: "", disable_password_changing: false,
        result: "", return_url: "", custom_properties: {}, ...model,
      }
    ),
};

// 機房管理(DCK-03-01)— DcRoom 三層結構(ticket 08;DTO 依 8/11 smoke 實測定版)。
// Room 走資源工廠;Floor/Area 是 controller 下的動作端點(非獨立資源)。
// CanDelete 為刪除前檢查:{ can_delete, rack_count, hardware_count, sample_*_nos }。
export const dcRoomsApi = {
  ...createBackendResourceApi(url.DCK_BACKEND.ROOM, { dto: "DcRoomDto" }),
  canDelete: (id) => api.get(`${url.DCK_BACKEND.ROOM}/CanDelete/${id}`),
  getFloors: (roomId) => api.get(`${url.DCK_BACKEND.ROOM}/GetFloors/${roomId}`),
  createFloor: (data) => api.create(`${url.DCK_BACKEND.ROOM}/CreateFloor`, outboundFor("DcFloorDto")(data)),
  updateFloor: (data) => api.update(`${url.DCK_BACKEND.ROOM}/UpdateFloor`, outboundFor("DcFloorDto")(data)),
  removeFloor: (id) => api.delete(`${url.DCK_BACKEND.ROOM}/DeleteFloor/${id}`),
  getAreas: (floorId) => api.get(`${url.DCK_BACKEND.ROOM}/GetAreas/${floorId}`),
  getAreasByRoom: (roomId) => api.get(`${url.DCK_BACKEND.ROOM}/GetAreasByRoom/${roomId}`),
  createArea: (data) => api.create(`${url.DCK_BACKEND.ROOM}/CreateArea`, outboundFor("DcAreaDto")(data)),
  updateArea: (data) => api.update(`${url.DCK_BACKEND.ROOM}/UpdateArea`, outboundFor("DcAreaDto")(data)),
  removeArea: (id) => api.delete(`${url.DCK_BACKEND.ROOM}/DeleteArea/${id}`),
};

// 營運資訊－IP 資訊(DCK-04-01;票 9月交付/16)— DcIpAddress(9/14 swagger,SIT 實測 9/13)。
// GetAll query: keyword(IP／Interface／Asset／MAC)、managerCustomerId／roomId／floorId／areaId
//   (0=不限)、pageIndex、pageSize → 分頁 DcIpAddressListItemDto(含後端算好的 status_text／
//   primary_asset_*／room_name／support_project_name／maintenance_vendor_name)
// Create/Update body: DcIpAddressDto(additionalProperties:false;必填只寫在 description:
//   ip_address／parent_ip_address／subnet_mask／default_gateway／manager_customer_id)
// 關聯是**子端點**不是實體欄位:asset_mappings 唯讀,寫入走 Add／RemoveAssetMapping 各一支
//   (AddAssetMapping 是 POST **query** 三參數、無 body;RemoveAssetMapping/{mappingId})。
// ConfirmReview/{id}:PUT 無 body,清 pending_review(「異動中」= SNMP 自動更新待人工確認)。
// ⚠️ Delete 只宣告 200／400／404,**沒有 409 也沒有 CanDelete**(票 28)。
const queryString = (params) =>
  Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
export const dcIpAddressesApi = {
  ...createBackendResourceApi(url.DCK_BACKEND.IP_ADDRESS, { dto: "DcIpAddressDto" }),
  getAssetMappings: (ipAddressId) =>
    api.get(`${url.DCK_BACKEND.IP_ADDRESS}/GetAssetMappings`, { params: { ipAddressId } }),
  // assetType 是 DcAssetType 列舉字串(Hardware／Rack／Software;2026-10-21 起可掛軟體)。
  // isPrimary(選填,預設 false):設為該資產的主要 IP,後端把它在其他 IP 的關聯改為次要
  addAssetMapping: ({ ipAddressId, assetType, assetId, isPrimary }) =>
    api.create(`${url.DCK_BACKEND.IP_ADDRESS}/AddAssetMapping?${queryString({ ipAddressId, assetType, assetId, ...(isPrimary ? { isPrimary: true } : {}) })}`),
  removeAssetMapping: (mappingId) =>
    api.delete(`${url.DCK_BACKEND.IP_ADDRESS}/RemoveAssetMapping/${mappingId}`),
  // 全部選填:assetName／roomId／assetType(null 或 All＝硬體＋機櫃)／brand／model／
  // managerCustomerId／ip(僅硬體)／ipAddressId(正在編輯的 IP,帶了每筆才附 IP 本身資訊)
  searchLinkableAssets: (params = {}) =>
    api.get(`${url.DCK_BACKEND.IP_ADDRESS}/SearchLinkableAssets`, { params }),
  getChangeHistory: (ipAddressId) =>
    api.get(`${url.DCK_BACKEND.IP_ADDRESS}/GetChangeHistory`, { params: { ipAddressId } }),
  confirmReview: (id) => api.update(`${url.DCK_BACKEND.IP_ADDRESS}/ConfirmReview/${id}`),
};

// 營運資訊－合約資訊(DCK-04-03;票 9月交付/33 讀取側)— DcContract(9/14 swagger,SIT 實測 9/13)。
// GetAll query: keyword、serviceLevelId／managerCustomerId／vendorId(0=不限)、startFrom／startTo／endFrom／endTo、
//   pageIndex、pageSize → 分頁 DcContractListItemDto(19 鍵;status_text／amount_text 後端算好,金額遮蔽由後端做)
// 子端點:GetRelations／GetAssetMappings／GetMaintenanceRecords 走**路徑參數** {contractId},GetChangeHistory 走 query
//   contractId,SearchLinkableAssets 沒有 contractId(全部選填)。
// 寫入端點(Create／Update／Delete／Add・RemoveAssetMapping／維護紀錄增改刪與附件)歸票 18,這裡先不包 ——
// 頁面寫入在票 18 前仍走 mock /api/contracts(只在 development 露出,見 ContractsPage)。
// 寫入：Update／Delete 走工廠（白名單 DcContractDto；Update 400 是 string[]，原本就採用）。
// Create：2026-10-20 接 DcVendor（選服務廠商 vendor_id）後開放；SIT 寫入未實測（要使用者同意）。
const contractWriteApi = createBackendResourceApi(url.DCK_BACKEND.CONTRACT, { dto: "DcContractDto" });
export const dcContractsApi = {
  create: contractWriteApi.create,
  update: contractWriteApi.update,
  remove: contractWriteApi.remove,
  getAll: (params = {}) => api.get(`${url.DCK_BACKEND.CONTRACT}/GetAll`, { params }),
  getById: (id) => api.get(`${url.DCK_BACKEND.CONTRACT}/GetById/${id}`),
  getRelations: (contractId) => api.get(`${url.DCK_BACKEND.CONTRACT}/GetRelations/${contractId}`),
  getAssetMappings: (contractId) => api.get(`${url.DCK_BACKEND.CONTRACT}/GetAssetMappings/${contractId}`),
  getChangeHistory: (contractId) =>
    api.get(`${url.DCK_BACKEND.CONTRACT}/GetChangeHistory`, { params: { contractId } }),
  // scheduledFrom／scheduledTo 選填(月曆只要該月可帶區間;不帶＝全部)
  getMaintenanceRecords: (contractId, params = {}) =>
    api.get(`${url.DCK_BACKEND.CONTRACT}/GetMaintenanceRecords/${contractId}`, { params }),
  searchLinkableAssets: (params = {}) =>
    api.get(`${url.DCK_BACKEND.CONTRACT}/SearchLinkableAssets`, { params }),
};

// 營運資訊－廠商（DcVendor，10/17 swagger；2026-10-20 SIT 唯讀實測）—— **只接讀取側**。
// 合約新增要選服務廠商（DcContractDto.vendor_id 必填），所以先接 GetAll／GetById／GetContracts。
// GetAll query：keyword、vendorTypeId／ratingId／managerCustomerId（0＝不限）、pageIndex、pageSize
//   → 分頁 DcVendorListItemDto（14 鍵；status_text 是「N份」承做合約數）。
// 寫入（Create／Update／Delete／AddContract）與廠商頁還沒接（廠商頁在部署站 devOnly）。
/**
 * 資產關聯(DcAssetRelation,2026-10-21 swagger)。assetType 是 DcAssetType 列舉字串(Rack／Hardware／Software);
 * 唯獨 SaveIps 的 body `asset_type` 是**整數**(swagger 如此),由 `assetRelationContract.saveIpsRequest` 轉碼。
 * 2026-10-21 SIT 唯讀實測:四支 GET 形狀與 swagger 一致;查無資產回 404 純字串「資產不存在 assetType=2, …」。
 * 寫入(Add*／Remove*／SaveIps／SetPrimaryIp／*OpenPort)還沒在 SIT 打過 —— 第一次寫入成功後覆核(ADR-0004)。
 */
const relationQuery = (assetType, assetId, extra = {}) => ({ params: { assetType, assetId, ...extra } });
export const dcAssetRelationApi = {
  getProjects: (assetType, assetId) => api.get(`${url.DCK_BACKEND.ASSET_RELATION}/GetProjects`, relationQuery(assetType, assetId)),
  addProject: (assetType, assetId, projectId) =>
    api.create(`${url.DCK_BACKEND.ASSET_RELATION}/AddProject`, undefined, relationQuery(assetType, assetId, { projectId })),
  removeProject: (assetType, assetId, projectId) =>
    api.delete(`${url.DCK_BACKEND.ASSET_RELATION}/RemoveProject`, relationQuery(assetType, assetId, { projectId })),
  getContracts: (assetType, assetId) => api.get(`${url.DCK_BACKEND.ASSET_RELATION}/GetContracts`, relationQuery(assetType, assetId)),
  addContract: (assetType, assetId, contractId) =>
    api.create(`${url.DCK_BACKEND.ASSET_RELATION}/AddContract`, undefined, relationQuery(assetType, assetId, { contractId })),
  removeContract: (assetType, assetId, contractId) =>
    api.delete(`${url.DCK_BACKEND.ASSET_RELATION}/RemoveContract`, relationQuery(assetType, assetId, { contractId })),
  // 主要 IP 排最前面
  getIps: (assetType, assetId) => api.get(`${url.DCK_BACKEND.ASSET_RELATION}/GetIps`, relationQuery(assetType, assetId)),
  // 以完整清單覆蓋(未列入的既有關聯會被移除;只能一個主要 IP)。body 用 saveIpsRequest() 產
  saveIps: (request) => api.update(`${url.DCK_BACKEND.ASSET_RELATION}/SaveIps`, outboundFor("DcAssetRelationSaveIpsRequest")(request)),
  setPrimaryIp: (assetType, assetId, ipAddressId) =>
    api.create(`${url.DCK_BACKEND.ASSET_RELATION}/SetPrimaryIp`, undefined, relationQuery(assetType, assetId, { ipAddressId })),
  getOpenPorts: (softwareId) => api.get(`${url.DCK_BACKEND.ASSET_RELATION}/GetOpenPorts/${softwareId}`),
  // IP 必須已關聯至該軟體,否則 400(純字串原因)
  addOpenPort: (dto) => api.create(`${url.DCK_BACKEND.ASSET_RELATION}/AddOpenPort`, outboundFor("DcSoftwareOpenPortDto")(dto)),
  updateOpenPort: (dto) => api.update(`${url.DCK_BACKEND.ASSET_RELATION}/UpdateOpenPort`, outboundFor("DcSoftwareOpenPortDto")(dto)),
  deleteOpenPort: (id) => api.delete(`${url.DCK_BACKEND.ASSET_RELATION}/DeleteOpenPort/${id}`),
};

export const dcVendorsApi = {
  getAll: (params = {}) => api.get(`${url.DCK_BACKEND.VENDOR}/GetAll`, { params }),
  getById: (id) => api.get(`${url.DCK_BACKEND.VENDOR}/GetById/${id}`),
  getContracts: (vendorId) => api.get(`${url.DCK_BACKEND.VENDOR}/GetContracts`, { params: { vendorId } }),
};

// 訊息功能－各類訊息設定(DCK-07-01-01;10/5 A 方案)— DcNotificationRule(9/29 swagger)。
// ⚠️ 規則 DTO 的值語意 SIT 沒有樣本(0 筆規則),照 swagger 寫;smoke 的 probeNotifyRuleRoundTrip 跑過後覆核(ADR-0004)。
// 轉成畫面形狀在 helpers/notifyRuleContract.js。
export const dcNotificationRulesApi = {
  getAll: (params = {}) => api.get(`${url.DCK_BACKEND.NOTIFICATION_RULE}/GetAll`, { params }),
  getCategoryCounts: () => api.get(`${url.DCK_BACKEND.NOTIFICATION_RULE}/GetCategoryCounts`),
  getById: (id) => api.get(`${url.DCK_BACKEND.NOTIFICATION_RULE}/GetById/${id}`),
  // 新增／編輯(10/5 round-trip 實測:Create 直接 Active、不送審);body＝DcNotificationRuleDto
  // 400 body 是 string(swagger),內容是驗證原因 → 讓 translateApiError 採用
  create: (dto) => api.create(`${url.DCK_BACKEND.NOTIFICATION_RULE}/Create`, dto),
  update: (dto) => api.update(`${url.DCK_BACKEND.NOTIFICATION_RULE}/Update`, dto),
  // 精靈送出前取該類(到期依子類)的觸發子項,triggers[] 以後端回的為底
  getTriggerOptions: (category, subTypeKey) =>
    api.get(`${url.DCK_BACKEND.NOTIFICATION_RULE}/GetTriggerOptions`, { params: { category, subTypeKey: subTypeKey || undefined } }),
  // 暫停／恢復:不送審(spec 共識 1);參數走 query,無 body
  setPaused: (id, paused) => api.create(`${url.DCK_BACKEND.NOTIFICATION_RULE}/SetPaused?${queryString({ id, paused })}`),
  remove: (id) => api.delete(`${url.DCK_BACKEND.NOTIFICATION_RULE}/Delete/${id}`),
  getSystemEvents: () => api.get(`${url.DCK_BACKEND.NOTIFICATION_RULE}/GetSystemEvents`),
  // body 是整份 DcSystemEventSettingDto[](swagger);回傳存好的陣列
  saveSystemEvents: (rows) => api.create(`${url.DCK_BACKEND.NOTIFICATION_RULE}/SaveSystemEvents`, rows),
};

// 資產管理－機櫃(DCK-05-01;票 20)— DcRack。
// GetAll query: roomId／businessCategoryId／managerCustomerId(0=不限)、keyword、
//   active、pageIndex、pageSize(預設 Int32.Max → **前端必帶**)→ 分頁 DcRackDto
// CanDelete/Delete 的阻擋 DTO 是 RackDeletionCheckResultDto
//   { can_delete, hardware_count, sample_hardware_nos[] } —— 沒有機櫃樣本欄,
//   與 DcRoom 的 RoomDeletionCheckResultDto **不是**同一個型別,不要共用。
// ⚠️ 契約來源是 swagger v1.00(文件層),8/12 smoke 時 runtime 仍 500 ——
// 依 ADR-0004 這叫「照文件寫」不是「抄實測」,第一次跑得動時要用 smoke 覆核。
export const dcRacksApi = {
  ...createBackendResourceApi(url.DCK_BACKEND.RACK, { dto: "DcRackDto" }),
  canDelete: (id) => api.get(`${url.DCK_BACKEND.RACK}/CanDelete/${id}`),
  /** 設定移轉中(2026-10-21):transferring=true 移轉中、false 移轉完成;回 DcRackDto */
  setTransferring: (id, transferring = true) =>
    api.create(`${url.DCK_BACKEND.RACK}/SetTransferring/${id}`, undefined, { params: { transferring } }),
};

/**
 * 資產管理－硬體(DCK-05-02;票 21)— DcHardware。
 *
 * GetAll query: roomId／rackId／managerCustomerId(0=不限)、keywords(可重複傳,
 *   keywords=Dell&keywords=VM 做交叉查詢;原單數 keyword 已更名)、
 *   riskTypes[]／riskMonths／riskStartDate／riskEndDate／riskLevels[]／
 *   businessCategoryIds[]／onlyKeyBusiness、pageIndex／pageSize(預設 20)
 *   → 分頁 DcHardwareListItemDto
 * 參數怎麼組**不在這裡** —— 由 `helpers/hardwareContract.js` 的 `hardwareQuery()`
 * 負責(空條件不送 key、等級轉數字、起訖優先於月數),這層只管把它送出去。
 *
 * ⚠️ **沒有 CanDelete**:機房與機櫃都有刪除前檢查,硬體這支後端沒給 ——
 * 所以「有軟體部署在上面」之類的阻擋只能靠 Delete 的錯誤回應,
 * 做不出機櫃那種「列出擋住的東西 + 指路」的彈窗。缺口記在票 21。
 *
 * ⚠️ 契約來源是 8/17 規格書(文件層),8/12 smoke 時 GetAll 仍 500 ——
 * 依 ADR-0004 這叫「照文件寫」不是「抄實測」,跑得動時要用 smoke 覆核,
 * **風險等級與 status_name 兩個查表鍵的實際值必驗**。
 */
export const dcHardwaresApi = {
  ...createBackendResourceApi(url.DCK_BACKEND.HARDWARE, { dto: "DcHardwareDto" }),
  // UAT #30:刪除前檢查——硬體是否有軟體部署在上面。
  // 後端預期回 HardwareDeletionCheckResultDto { can_delete, software_count, sample_software_nos[] }。
  // ⚠️ 後端尚未實作（原註解「沒有 CanDelete」）;前端先搭好,404/501 靜默略過、
  // 降級為「不擋刪除」（=現行行為）,等後端補上即自動生效。
  canDelete: (id) => api.get(`${url.DCK_BACKEND.HARDWARE}/CanDelete/${id}`),
};

/**
 * 批次匯入－DC Keeper 標準範本。
 * 檔案原樣以 multipart `file` 送後端,前端不解析;回 ImportResultDto(投影見 helpers/importContract.js)。
 * `createMissingReferences` 的政策集中在 importContract.js(UAT 假設 true)。
 */
const importFile = (action) => (file) => {
  const fd = new FormData();
  fd.append("file", file, file.name);
  return api.upload(`${url.DCK_BACKEND.IMPORT}/${action}`, fd, { createMissingReferences: CREATE_MISSING_REFERENCES });
};
/** 合約／IP 的匯入端點沒有 createMissingReferences 參數(swagger 10/21),不送 */
const importFileNoRefs = (action) => (file) => {
  const fd = new FormData();
  fd.append("file", file, file.name);
  return api.upload(`${url.DCK_BACKEND.IMPORT}/${action}`, fd);
};
/**
 * 下載後端範本(2026-10-21,6 種:Room／Rack／Hardware／Software／Contract／Ip)→ Blob。
 * 範本以後端為準 —— 前端自己產的標題列跟後端欄序對不上就會整份錯位(UAT DCK-BUG-042)。
 */
const downloadSample = (kind) => () =>
  api.get(`${url.DCK_BACKEND.IMPORT}/Download${kind}ImportSample`, { responseType: "blob" });
export const dcImportApi = {
  dckeeperContracts: importFileNoRefs("ImportDcKeeperContracts"),
  dckeeperIpAddresses: importFileNoRefs("ImportDcKeeperIpAddresses"),
  roomSample: downloadSample("Room"),
  rackSample: downloadSample("Rack"),
  hardwareSample: downloadSample("Hardware"),
  softwareSample: downloadSample("Software"),
  contractSample: downloadSample("Contract"),
  ipSample: downloadSample("Ip"),
  dckeeperRooms: importFile("ImportDcKeeperRooms"),
  dckeeperRacks: importFile("ImportDcKeeperRacks"),
  dckeeperHardware: importFile("ImportDcKeeperHardware"),
  dckeeperSoftware: importFile("ImportDcKeeperSoftware"),
};

/**
 * 資產管理－軟體(DCK-05-03;票 35)— DcSoftware。
 *
 * GetAll query 與硬體同一組,外加 `environmentId`（Common/OperatingEnvironments 的 int 1–4）。
 * 畫面下拉給的就是這個 id；`softwareQuery()` 原樣送出。寫入用 `environment_id`。
 *
 * CanDelete 回 `SoftwareDeletionCheckResultDto`
 *   { can_delete, sample_hardware_ids[] } —— **只有 id、沒有編號**,
 *   與機櫃的 sample_hardware_nos 不是同一個型別。
 *
 * 部署設備:
 *   GetDeployments 只回 { software_id, hardware_id, id }（畫不出畫面）——
 *   列表展開改用 GetAll 的 `deployments[]`（Q2 路徑 a）。
 *   AddDeployment／RemoveDeployment 是 **GET 但會寫入**。Q1 未改成 POST/DELETE
 *   之前,這裡帶 `Cache-Control: no-store` 與 `_` cache-buster,避免瀏覽器
 *   預取／快取把同一台加兩次或移除後加不回去。
 *   ⚠️ smoke **不准打**這兩支（同票 14 的教訓）。
 */
export const dcSoftwaresApi = {
  ...createBackendResourceApi(url.DCK_BACKEND.SOFTWARE, { dto: "DcSoftwareDto" }),
  canDelete: (id) => api.get(`${url.DCK_BACKEND.SOFTWARE}/CanDelete/${id}`),
  getDeployments: (softwareId) =>
    api.get(`${url.DCK_BACKEND.SOFTWARE}/GetDeployments/${softwareId}`),
  addDeployment: (softwareId, hardwareId) =>
    api.get(`${url.DCK_BACKEND.SOFTWARE}/AddDeployment/${softwareId}/${hardwareId}`, {
      params: { _: Date.now() },
      headers: { "Cache-Control": "no-store" },
    }),
  removeDeployment: (softwareId, hardwareId) =>
    api.get(`${url.DCK_BACKEND.SOFTWARE}/RemoveDeployment/${softwareId}/${hardwareId}`, {
      params: { _: Date.now() },
      headers: { "Cache-Control": "no-store" },
    }),
};

// 後台左選單(ADR-0003) — GET /api-backend/AdminMenu/GetMenu?showHidden=
// 200: AdminMenuItemDto[](system_name 映前端路由,可遞迴 child_nodes)
export const adminMenuApi = {
  getMenu: (params = {}) => api.get(`${url.DCK_BACKEND.ADMIN_MENU}/GetMenu`, { params }),
};

/**
 * 資產版本／弱點爬蟲（票 29）—— **只讀,不寫**。
 *
 * `GetVulnerabilities` 回 `DcVulnerabilityDto` 的分頁封套;
 * `GetRecentRuns` 回 `DcScraperRunDto[]`(爬蟲最近幾次執行,用來講「資料多新」)。
 *
 * `pageSize` 一定要給:文件的預設是 `2147483647`,不給就是整包倒出來 ——
 * CVE 目錄會長,看板拿去渲染會直接把瀏覽器打爆。
 *
 * 同 controller 的 `UpdateVersion`／`ReportFailure` 是爬蟲的寫回入口(建 run、
 * 發失效告警),**前端一支都不接**;不要因為「順手」把它們加進這裡。
 */
export const assetVersionApi = {
  getVulnerabilities: ({ keyword = "", isKev, pageIndex = 0, pageSize = 200 } = {}) =>
    api.get(`${url.DCK_BACKEND.ASSET_VERSION}/GetVulnerabilities`, {
      params: { keyword, ...(isKev === undefined ? {} : { isKev }), pageIndex, pageSize },
    }),
  // 爬蟲執行紀錄。8/17 規格書補上 `DcScraperRunDto` 後已接進智慧看板的
  // 「資料更新於」那一行(票 29 第二個缺口)。形狀仍待 smoke 覆核 —— 目前是
  // 照文件寫的,實測跑過再定版(ADR-0004)。
  getRecentRuns: (params = {}) =>
    api.get(`${url.DCK_BACKEND.ASSET_VERSION}/GetRecentRuns`, { params }),
  // NVD CPE 字典候選(指派資產 CPE 用)—— 200: CpeSuggestionDto[] { cpe_name, title, deprecated }
  suggestCpe: ({ keyword, take = 20, includeDeprecated = false }) =>
    api.get(`${url.DCK_BACKEND.ASSET_VERSION}/SuggestCpe`, { params: { keyword, take, includeDeprecated } }),
};

// 通用資料 — GET /api-backend/Common/PhoneCountryCodes
// 200: PhoneCountryCodeItem[] { value, text }(使用者表單手機國碼下拉)
export const commonApi = {
  phoneCountryCodes: () => api.get(`${url.DCK_BACKEND.COMMON}/PhoneCountryCodes`),
  // 票 27:三支清單**形狀不一樣**,不要共用型別 ——
  //   Currencies        → CurrencyModelDto  { name, currency_code }
  //   BusinessCategories→ DcRackOption      { value:int, text }
  //   AssetCategories   → DcRackOption      { value:int, text }
  // 幣別沒有 value/text,照抄國碼那支的用法會整排空白。
  currencies: () => api.get(`${url.DCK_BACKEND.COMMON}/Currencies`),
  businessCategories: () => api.get(`${url.DCK_BACKEND.COMMON}/BusinessCategories`),
  assetCategories: () => api.get(`${url.DCK_BACKEND.COMMON}/AssetCategories`),
  // 2026-08-17 規格書新增。兩支都回 DcAssetOption { value:int, text } ——
  // 與 BusinessCategories/AssetCategories 同型,與 Currencies 不同型(那支是 name/currency_code)。
  // 營運環境對應 DcOperatingEnvironment enum(Production/Testing/Development/DisasterRecovery),
  // 風險等級對應 4嚴重/3高/2中/1低 —— 兩者都是硬體/軟體查詢面板要用的。
  operatingEnvironments: () => api.get(`${url.DCK_BACKEND.COMMON}/OperatingEnvironments`),
  // 票 27(2026-09-13 SIT 交付):合約服務等級／維護週期,同樣是 { value:int, text }。
  // 值定版見 docs/api/smoke-SIT-20260913.md;合約頁的下拉與篩選改吃這兩支,不再寫死。
  serviceLevels: () => api.get(`${url.DCK_BACKEND.COMMON}/ServiceLevels`),
  maintenanceCycles: () => api.get(`${url.DCK_BACKEND.COMMON}/MaintenanceCycles`),
  riskLevels: () => api.get(`${url.DCK_BACKEND.COMMON}/RiskLevels`),
  // 廠商選單（2026-10-20）：VendorTypes／VendorEngineerTypes 是 { value, text }；
  // VendorRatings 多一個 description（評等說明，SIT 實測「A：戰略夥伴」＋說明句）。
  vendorTypes: () => api.get(`${url.DCK_BACKEND.COMMON}/VendorTypes`),
  vendorRatings: () => api.get(`${url.DCK_BACKEND.COMMON}/VendorRatings`),
  vendorEngineerTypes: () => api.get(`${url.DCK_BACKEND.COMMON}/VendorEngineerTypes`),
};

/**
 * 智慧看板（票 24／43）—— **只讀,不寫**。
 *
 * 七支 GET 對應待辦與硬體／軟體風險卡。`take` 一定要給：EOS／EOL／CVE／KEV
 * 明細與待辦 swagger 預設都是 10 —— 不給就吃後端預設,看板捲動區會假裝沒有更多。
 * `assetType` 顯式送 Hardware／Software,不要省略讓後端用 All 混在一起。
 */
export const dashboardApi = {
  getTodo: () => api.get(`${url.DCK_BACKEND.DASHBOARD}/GetTodo`),
  getTodoItems: ({ take = 10, expiringDays = 90 } = {}) =>
    api.get(`${url.DCK_BACKEND.DASHBOARD}/GetTodoItems`, {
      params: { take, expiringDays },
    }),
  getAssetRiskSummary: ({ assetType, onlyKeyBusiness = false } = {}) =>
    api.get(`${url.DCK_BACKEND.DASHBOARD}/GetAssetRiskSummary`, {
      params: { assetType, onlyKeyBusiness },
    }),
  getEosDetails: ({ assetType, onlyKeyBusiness = true, take = 10 } = {}) =>
    api.get(`${url.DCK_BACKEND.DASHBOARD}/GetEosDetails`, {
      params: { assetType, onlyKeyBusiness, take },
    }),
  getEolDetails: ({ assetType, onlyKeyBusiness = true, take = 10 } = {}) =>
    api.get(`${url.DCK_BACKEND.DASHBOARD}/GetEolDetails`, {
      params: { assetType, onlyKeyBusiness, take },
    }),
  getCveDetails: ({ assetType, onlyKeyBusiness = false, take = 10 } = {}) =>
    api.get(`${url.DCK_BACKEND.DASHBOARD}/GetCveDetails`, {
      params: { assetType, onlyKeyBusiness, take },
    }),
  getKevDetails: ({ assetType, onlyKeyBusiness = false, take = 10 } = {}) =>
    api.get(`${url.DCK_BACKEND.DASHBOARD}/GetKevDetails`, {
      params: { assetType, onlyKeyBusiness, take },
    }),
};

/**
 * 廠牌／型號主檔（票 32;2026-08-17 規格書新增）—— **全部唯讀**。
 *
 * 沒有 Create/Update/Delete:主檔由後端維護(型號的規格欄位還會被爬蟲寫回,
 * 見 DcModelDto 的 last_scraped_on_utc/data_source)。前端只查。
 *
 * 這支是硬體(票 21)與軟體的**前置件**:兩邊的 DTO 都改用 `brand_id`／`model_id`
 * 指向主檔,不再是自由文字。型號還帶著一整組規格(U 數、電源數、CPU、EOS/EOL…),
 * 硬體編輯頁的「由廠牌型號自動帶入」就是讀這裡。
 *
 * `assetType` 是 DcAssetType enum(All/Rack/Hardware/Software)—— 查機櫃能用的型號
 * 就傳 Rack,不要把軟體型號也撈出來。
 */
export const dcBrandsApi = {
  getAll: (params = {}) =>
    api.get(`${url.DCK_BACKEND.BRAND}/GetAll`, {
      params: { pageIndex: 0, pageSize: 500, ...params },
    }),
  getById: (id) => api.get(`${url.DCK_BACKEND.BRAND}/GetById/${id}`),
  getModels: (params = {}) =>
    api.get(`${url.DCK_BACKEND.BRAND}/GetModels`, {
      params: { pageIndex: 0, pageSize: 500, ...params },
    }),
  getModelById: (id) => api.get(`${url.DCK_BACKEND.BRAND}/GetModelById/${id}`),
};

// 訊息功能(DCK-07;notify-api 票 03–06)— DcMessage(9/29 swagger,9/30・10/1 SIT smoke 定版)。
// GetAll query: targetCustomerId、messageCategory(enum 名稱 DataIncomplete…VersionUpdate)、keyword、
//   isRead、pageIndex、pageSize → 分頁 DcMessageDto。**不帶 targetCustomerId＝全部人的訊息** ——
//   呼叫端一律帶登入者(spec 共識 3),這層不替它補,漏帶要在呼叫端看得出來。
// GetCategoryCounts → [{ value:1..6, text, count }](swagger 沒寫型別,形狀以實測為準)
// GetUnreadCount(targetCustomerId 必填)→ 整數
// MarkAsRead／MarkAllAsRead 都是 POST **query** 參數、無 body;MarkAllAsRead 回清掉的筆數
export const dcMessagesApi = {
  getAll: (params = {}) => api.get(`${url.DCK_BACKEND.MESSAGE}/GetAll`, { params }),
  getCategoryCounts: (params = {}) => api.get(`${url.DCK_BACKEND.MESSAGE}/GetCategoryCounts`, { params }),
  getUnreadCount: (targetCustomerId) =>
    api.get(`${url.DCK_BACKEND.MESSAGE}/GetUnreadCount`, { params: { targetCustomerId } }),
  markAsRead: (id) => api.create(`${url.DCK_BACKEND.MESSAGE}/MarkAsRead?${queryString({ id })}`),
  markAllAsRead: ({ targetCustomerId, messageCategory } = {}) =>
    api.create(`${url.DCK_BACKEND.MESSAGE}/MarkAllAsRead?${queryString({ targetCustomerId, messageCategory })}`),
};
